import { PageHeading } from '@src/components/PageHeading/PageHeading';
import './ParticipantCard.css';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, FullCardInfo } from '@lub0v/santa-utils';
import {
  Button,
  Container,
  GameBadge,
  CheckIcon,
  Confetti,
  HintError,
  HiddenFace,
  Note,
  ParticipantFace,
  PersonCard,
  Stack,
  Typography,
} from '@src/components';
import { santaClient } from '@src/state/client';

/** How long the paper keeps falling after the giftee is revealed. */
const CELEBRATION_MS = 5000;

export interface ParticipantCardProps {
  info: BoxInfo;
  card: FullCardInfo;
  ward: FullCardInfo | null;
  onChanged: () => Promise<void> | void;
}

/**
 * One participant's own screen: who they are, and — once they ask — who they
 * are giving a gift to. The giftee stays hidden until then, so that a card
 * opened in front of other people gives nothing away.
 */
export function ParticipantCard({ info, card, ward, onChanged }: ParticipantCardProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [celebrating, setCelebrating] = useState(false);

  const revealed = Boolean(card.isWardKnown && ward);

  useEffect(() => {
    if (!celebrating) {
      return undefined;
    }
    const stop = window.setTimeout(() => setCelebrating(false), CELEBRATION_MS);
    return () => window.clearTimeout(stop);
  }, [celebrating]);

  async function reveal() {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Revealing is a change to the card, not a read: the organizer's list
      // shows who has already looked.
      const result = await santaClient.saveCard({
        key: info.box.key,
        username: card.username,
        isWardKnown: true,
      });
      if (!result.ok) {
        setError(result.error?.message ?? 'errors.UNKNOWN');
        return;
      }
      setCelebrating(true);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Container width="measure" grow className="quick-recipient" testId="box-card">
        <header className="quick-recipient__heading">
          <GameBadge name={info.box.name} testId="box-card-badge" />
          <PageHeading testId="box-card-title" title={<>{t('box.card.title', { name: card.username })}</>} />
        </header>
        <PersonCard
          edges={3}
          className="quick-recipient__card"
          avatar={
            revealed && ward ? (
              <ParticipantFace card={ward} size="identity" />
            ) : (
              <HiddenFace label={t('box.card.hiddenLabel')} testId="box-card-hidden" />
            )
          }
          name={revealed && ward ? ward.username : ''}
          testId="box-card-panel"
        >
          <Typography as="p" role="body/base" color="inherit" testId="box-card-lead">
            {t('box.card.hint')}
          </Typography>

          <Stack gap={3} wide>
            <HintError error={error} testId="box-card-error" />

            {revealed ? (
              <Note tone="success" icon={<CheckIcon />} iconAlign="start" testId="box-card-done">
                {t('box.card.done')}
              </Note>
            ) : (
              <Button
                variant="primary"
                size="large"
                loading={busy}
                disabled={Boolean(info.box.isArchived)}
                onClick={() => void reveal()}
                testId="box-card-reveal"
              >
                {t('box.card.reveal')}
              </Button>
            )}
          </Stack>
        </PersonCard>
      </Container>
      <Confetti active={celebrating} testId="box-card-confetti" />
    </>
  );
}
